import { floorLighting, lightingDisplayPlan } from '../engine/lightingVisual.js';
import { lightPose } from '../engine/lightingData.js';
import { isCeilingCamera, isCeilingKind, isPoleCamera, isPoleKind, placementMountKind } from '../engine/cameraMounts.js';
import { snapPoleCamera, poleCameraPose } from '../engine/poleMounting.js';
import { ceilingCameraPose, ceilingPlacement } from '../engine/ceilingMounting.js';
import { connectedStairs, onStairFootprint } from '../engine/stairs.js';
import { orderedSiteAreas } from '../engine/siteAreas.js';
import { useEffect, useState, useImperativeHandle, useRef, forwardRef, useCallback } from 'react';
import { usePlan } from '../store.js';
import { DEG, RAD, clamp, uid, fmt, norm360 } from '../engine/constants.js';
import { draw, readPalette, fitView, X, Y, WX, WY } from '../engine/render.js';
import {
  ptInPoly, polyArea, distToSeg, insideSite, polyCentroid
} from '../engine/geometry.js';
import { openingAt, placePlanObject, finishPlanDraft, wallEdit } from '../engine/editing.js';
import { openingCentre } from '../engine/siteGeometry.js';
import { personDimensions, presentInScene } from '../engine/sceneObjects.js';
import { snapWallCamera } from '../engine/mounting.js';
import { drawDesign } from '../engine/renderDesign.js';
import { camGeom } from '../engine/optics.js';
import { isUiEvent } from '../engine/keyboard.js';
import { formatLength } from '../engine/units.js';
import { groundSelection, siteGroundPlan } from '../engine/siteGround.js';

/**
 * The drawing surface. React owns the element and the lifecycle; everything
 * inside the canvas is imperative — pan, zoom, hit-testing and the draft
 * polyline all live in refs, because routing a pointermove through React state
 * would repaint the whole shell sixty times a second for no benefit.
 */
const Stage = forwardRef(function Stage(
  { store, readOnly = false, active = true, viewMode, showHeat, showFov, showShellReference = true, onHint, onCoords, bgImage },
  ref
) {
  usePlan(store);
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const view = useRef({ ox: -2, oy: -2, k: 30 });
  const zoomMinimum = useRef(1);
  useEffect(() => { zoomMinimum.current = 1; }, [store.doc.activeFloorId]);
  const size = useRef({ w: 1, h: 1 });
  const pal = useRef(null);
  const drag = useRef(null);
  const draft = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const hover = useRef(null);
  const spaceDown = useRef(false);
  const raf = useRef(0);
  const calib = useRef(null);

  let S = store.doc;

  /* ---- painting ---- */
  const paint = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!pal.current) pal.current = readPalette();
    const displayPlan = lightingDisplayPlan(store.lightingTime ? siteGroundPlan(store.lightingViewDoc) : store.groundViewDoc, viewMode);
    draw(ctx, {
      S: displayPlan,
      cov: store.cov, lighting: floorLighting(store),
      view: view.current,
      w: size.current.w,
      h: size.current.h,
      PAL: pal.current,
      labelMode: store.viewLabels || 'auto', viewMode, showHeat, showFov, showShellReference,
      sel: store.sel,
      draft: draft.current?.kind === 'cable' ? null : draft.current,
      hoverPt: hover.current,
      bgImg: bgImage
    });
    drawDesign(ctx, displayPlan, view.current, { labelMode: store.viewLabels || 'auto', probe: store.probe, selected: store.sel, draft: draft.current, hover: hover.current });
    if (calib.current) {
      const { a, b } = calib.current;
      ctx.save();
      ctx.strokeStyle = pal.current.accent; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(a.sx, a.sy); ctx.lineTo(b.sx, b.sy); ctx.stroke();
      const px = Math.hypot(b.sx - a.sx, b.sy - a.sy);
      ctx.font = '600 12px "IBM Plex Mono", monospace';
      ctx.fillStyle = pal.current.accent2;
      ctx.fillText(Math.round(px) + ' px', (a.sx + b.sx) / 2 + 10, (a.sy + b.sy) / 2 - 8);
      ctx.restore();
    }
  }, [store, viewMode, showHeat, showFov, showShellReference, bgImage]);

  const requestDraw = useCallback(() => {
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => { raf.current = 0; paint(); });
  }, [paint]);

  const resize = useCallback(() => {
    const cv = canvasRef.current, wrap = wrapRef.current;
    if (!cv || !wrap) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const r = wrap.getBoundingClientRect();
    // Keep the same world point centered when properties or the viewport resize.
    if (size.current.w > 1 && size.current.h > 1 && r.width > 1 && r.height > 1) {
      view.current.ox += (size.current.w - r.width) / (2 * view.current.k);
      view.current.oy += (size.current.h - r.height) / (2 * view.current.k);
    }
    cv.width = Math.max(1, Math.round(r.width * dpr));
    cv.height = Math.max(1, Math.round(r.height * dpr));
    cv.getContext('2d').setTransform(dpr, 0, 0, dpr, 0, 0);
    size.current = { w: r.width, h: r.height };
    requestDraw();
  }, [requestDraw]);

  useEffect(() => {
    resize();
    const ro = new ResizeObserver(resize);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener('resize', resize);
    return () => { ro.disconnect(); window.removeEventListener('resize', resize); };
  }, [resize]);

  useEffect(() => { if (draft.current && store.tool !== draft.current.kind) { draft.current = null; setDrawing(false); hover.current = null; requestDraw(); } }, [store.tool, requestDraw]);

  // repaint on any store revision, view mode or overlay toggle
  useEffect(requestDraw, [requestDraw, store.rev, viewMode, showHeat, showFov, bgImage]);

  useImperativeHandle(ref, () => ({
    repalette() { pal.current = readPalette(); requestDraw(); },
    fit() {
      zoomMinimum.current = 1;
      view.current = fitView(store.groundViewDoc, size.current.w, size.current.h, showShellReference);
      requestDraw();
      return view.current;
    },
    fitBounds(bounds) {
      if (!bounds || !['minX', 'minY', 'maxX', 'maxY'].every(key => Number.isFinite(bounds[key]) && Math.abs(bounds[key]) <= 100000)
          || bounds.maxX <= bounds.minX || bounds.maxY <= bounds.minY) return null;
      resize();
      const { w, h } = size.current;
      if (w < 2 || h < 2) return null;
      // Dynamic padding keeps the complete room visible on a short landscape
      // canvas. The lower zoom bound also accommodates a large warehouse.
      const padX = Math.min(72, w * .18), padY = Math.min(88, h * .22);
      const k = clamp(Math.min((w - 2 * padX) / Math.max(.1, bounds.maxX - bounds.minX),
        (h - 2 * padY) / Math.max(.1, bounds.maxY - bounds.minY)), .000001, 400);
      zoomMinimum.current = k < 1 ? .000001 : 1;
      view.current = { k, ox: (bounds.minX + bounds.maxX) / 2 - w / (2 * k), oy: (bounds.minY + bounds.maxY) / 2 - h / (2 * k) };
      requestDraw();
      return view.current;
    },
    zoomBy(f) {
      zoomAt(size.current.w / 2, size.current.h / 2, f);
    },
    centreOn(x, y) {
      view.current.ox = x - size.current.w / (2 * view.current.k);
      view.current.oy = y - size.current.h / (2 * view.current.k);
      requestDraw();
    },
    view: () => view.current,
    canvas: () => canvasRef.current,
    beginCalibrate() { calib.current = null; },
    beginCable(camId, equipmentId, routeId) { draft.current = { kind: 'cable', pts: [], closed: false, camId, equipmentId, routeId }; store.setTool('cable'); setDrawing(true); canvasRef.current?.focus({ preventScroll: true }); requestDraw(); }
  }), [store, requestDraw, resize]);

  function zoomAt(sx, sy, factor) {
    const v = view.current;
    const wx = WX(v, sx), wy = WY(v, sy);
    // A warehouse fit widens this view’s range until another fit/floor change,
    // including after zooming in above 1 px/m and then back out again.
    v.k = clamp(v.k * factor, zoomMinimum.current, 500);
    v.ox = wx - sx / v.k;
    v.oy = wy - sy / v.k;
    requestDraw();
    onHint?.({ zoom: Math.round(v.k / store.doc.opt.scale * 100) });
  }

  /* ---- snapping ---- */
  function snapPoint(x, y) {
    const v = view.current;
    if (!S.opt.snap) return { x, y };
    const tol = 10 / v.k;
    let best = null, bd = tol;
    const cand = [];
    for (const w of S.walls) cand.push({ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 });
    for (const a of S.areas) for (const p of a.pts) cand.push(p);
    for (const z of S.zones) for (const p of z.pts) cand.push(p);
    for (const p of cand) {
      const d = Math.hypot(p.x - x, p.y - y);
      if (d < bd) { bd = d; best = { x: p.x, y: p.y }; }
    }
    if (best) return best;
    const g = S.opt.grid;
    return { x: Math.round(x / g) * g, y: Math.round(y / g) * g };
  }

  function orthoSnap(from, to) {
    if (!S.opt.ortho || !from) return to;
    const dx = to.x - from.x, dy = to.y - from.y;
    if (Math.hypot(dx, dy) < 1e-6) return to;
    const a = Math.round(Math.atan2(dy, dx) * DEG / 45) * 45;
    const r = a * RAD;
    const proj = dx * Math.cos(r) + dy * Math.sin(r);
    return { x: from.x + Math.cos(r) * proj, y: from.y + Math.sin(r) * proj };
  }

  /* ---- hit testing ---- */
  function hitTest(x, y) {
    const S = store.groundViewDoc;
    const v = view.current;
    for (const l of S.lights || []) { const p = lightPose(S, l); if (Math.hypot(p.x - x, p.y - y) < 12 / v.k) return { type: 'light', id: l.id }; }
    for (const e of S.equipment) if (Math.hypot(e.x - x, e.y - y) < 12 / v.k) return { type: 'equipment', id: e.id };
    const tol = 9 / v.k;
    for (const c of S.cams) if (Math.hypot(c.x - x, c.y - y) < Math.max(tol, 12 / v.k)) return { type: 'cam', id: c.id };
    for (const t of [...S.targets].reverse()) {
      const a = (t.pan || 0) * RAD, dx = x - t.x, dy = y - t.y, localX = dx * Math.cos(a) + dy * Math.sin(a), localY = -dx * Math.sin(a) + dy * Math.cos(a);
      const h = personDimensions(S, t).height;
      if (Math.abs(localX - (t.pose === 'seated' ? h * 0.1 : 0)) <= (t.kind === 'vehicle' ? 2.2 : h * (t.pose === 'seated' ? 0.24 : 0.13)) + tol && Math.abs(localY) <= (t.kind === 'vehicle' ? 0.9 : h * 0.2) + tol) return { type: 'target', id: t.id };
    }
    for (const o of S.openings) { const c = openingCentre(S, o); if (c && Math.hypot(c.x - x, c.y - y) < Math.max(tol, o.width / 2)) return { type: 'opening', id: o.id }; }
    for (const stair of connectedStairs(S).slice().reverse()) if (onStairFootprint(stair, x, y, true, tol)) return { type: 'stair', id: stair.id };
    for (const o of [...S.obstacles].reverse()) {
      if (!presentInScene(o, S.sceneState)) continue;
      const a = (o.pan || 0) * RAD, dx = x - o.x, dy = y - o.y;
      if (Math.abs(dx * Math.cos(a) + dy * Math.sin(a)) <= o.width / 2 + tol && Math.abs(-dx * Math.sin(a) + dy * Math.cos(a)) <= o.depth / 2 + tol) return { type: 'obstacle', id: o.id };
    }
    for (const t of S.tasks) if (!t.openingId && distToSeg(x, y, t.x, t.y, t.x + Math.cos(t.heading * RAD) * (t.length || 1), t.y + Math.sin(t.heading * RAD) * (t.length || 1)) < tol) return { type: 'task', id: t.id };
    for (const w of S.walls) if (distToSeg(x, y, w.x1, w.y1, w.x2, w.y2) < Math.max(tol, 6 / v.k)) return { type: 'wall', id: w.id };
    for (let i = S.zones.length - 1; i >= 0; i--) if (ptInPoly(x, y, S.zones[i].pts)) return { type: 'zone', id: S.zones[i].id };
    const areas = [...S.areas.filter(a => a.kind === 'exclude').reverse(), ...orderedSiteAreas(S).reverse()];
    for (const a of areas) if (ptInPoly(x, y, a.pts)) return { type: 'area', id: a.id };
    if (S.bg && bgImage && !S.bg.locked) {
      const w = bgImage.naturalWidth * S.bg.mpp, h = bgImage.naturalHeight * S.bg.mpp;
      if (x >= S.bg.x && x <= S.bg.x + w && y >= S.bg.y && y <= S.bg.y + h) return { type: 'bg' };
    }
    return null;
  }

  function vertexAt(pts, x, y) {
    const tol = 8 / view.current.k;
    for (let i = 0; i < pts.length; i++) if (Math.hypot(pts[i].x - x, pts[i].y - y) < tol) return i;
    return -1;
  }

  /** Aim a freshly dropped camera away from the nearest wall, into the room. */
  function autoAim(x, y) {
    let best = null, bd = 2.5;
    for (const w of S.walls) {
      const d = distToSeg(x, y, w.x1, w.y1, w.x2, w.y2);
      if (d < bd) { bd = d; best = w; }
    }
    if (best) {
      const nx = -(best.y2 - best.y1), ny = best.x2 - best.x1;
      const L = Math.hypot(nx, ny) || 1;
      const a1 = Math.atan2(ny / L, nx / L) * DEG;
      const p1 = { x: x + Math.cos(a1 * RAD) * 1.5, y: y + Math.sin(a1 * RAD) * 1.5 };
      return insideSite(S, p1.x, p1.y) ? norm360(a1) : norm360(a1 + 180);
    }
    const fl = S.areas.find(a => a.kind === 'floor' && ptInPoly(x, y, a.pts))
      || S.areas.find(a => a.kind === 'floor');
    if (fl) {
      const c = polyCentroid(fl.pts);
      const a = Math.atan2(c.y - y, c.x - x) * DEG;
      return Number.isFinite(a) ? norm360(a) : 0;
    }
    return 0;
  }

  /* ---- pointer ---- */
  const evPt = e => {
    const r = canvasRef.current.getBoundingClientRect();
    return { sx: e.clientX - r.left, sy: e.clientY - r.top };
  };

  function onPointerDown(e) {
    const cv = canvasRef.current;
    cv.setPointerCapture(e.pointerId);
    const { sx, sy } = evPt(e);
    const v = view.current;
    const wx = WX(v, sx), wy = WY(v, sy);
    const tool = store.tool;

    if (e.button === 1 || spaceDown.current || tool === 'pan') {
      drag.current = { mode: 'pan', sx, sy, ox: v.ox, oy: v.oy };
      return;
    }
    if (e.button !== 0) return;
    if (tool === 'inspect') { store.probe = { x: wx, y: wy }; store.emit(); onHint?.({ inspected: true }); return; }
    if (readOnly) {
      store.select(hitTest(wx, wy));
      return;
    }

    if (tool === 'select') {
      if (['light', 'cam', 'obstacle', 'target', 'task', 'stair'].includes(store.sel?.type)) {
        const selected = store.selected(), c = store.sel.type === 'light' && selected ? lightPose(S, selected) : selected;
        if (c && !c.locked && !c.openingId && (store.sel.type !== 'obstacle' || presentInScene(c, S.sceneState))) {
          const gx = X(v, c.x) + Math.cos((c.heading ?? c.pan ?? 0) * RAD) * 34;
          const gy = Y(v, c.y) + Math.sin((c.heading ?? c.pan ?? 0) * RAD) * 34;
          if (Math.hypot(sx - gx, sy - gy) < 10) {
            store.snapshot();
            drag.current = { mode: 'aim', cam: selected, anchor: c, field: store.sel.type === 'task' ? 'heading' : 'pan' };
            return;
          }
        }
      }
      if (store.sel && ['area', 'zone', 'wall'].includes(store.sel.type)) {
        const o = store.selected();
        if (o) {
          const pts = store.sel.type === 'wall'
            ? [{ x: o.x1, y: o.y1 }, { x: o.x2, y: o.y2 }]
            : o.pts;
          const vi = vertexAt(pts, wx, wy);
          if (vi >= 0) { store.snapshot(); drag.current = { mode: 'vertex', obj: o, vi }; return; }
        }
      }
      const h = store.select(hitTest(wx, wy));
      S = store.doc;
      if (h) onHint?.({ selected: h });
      if (h && !(h.type === 'cam' && store.selected()?.locked) && !(h.type === 'task' && store.selected()?.openingId)) {
        store.snapshot();
        drag.current = {
          mode: 'move', start: { wx, wy }, type: h.type,
          obj: store.selected(),
          orig: JSON.parse(JSON.stringify(h.type === 'light' ? lightPose(S, store.selected()) : store.selected()))
        };
      }
      requestDraw();
      return;
    }

    if (!draft.current && !['calib', 'cable', 'building', 'outdoor', 'parking', 'road'].includes(tool)) {
      store.activateGroundAt({ x: wx, y: wy }); S = store.doc;
    }
    if (tool === 'camera') {
      let p = snapPoint(wx, wy);
      const modelId = store.model(store.newModelId)?.id || store.availableModels[0]?.id;
      const model = store.model(modelId);
      if (!model) { onHint?.({ message: 'Add a camera model first: open Camera library and add one to this site.' }); return; }
      const mountKind = placementMountKind(store, model);
      if (isCeilingKind(mountKind)) {
        p = ceilingPlacement(store, { ...p, modelId, mountKind }, model);
        if (!p) { onHint?.({ message: 'No ceiling here. Add a roof / ceiling or place beneath an upper floor slab.' }); return; }
      } else if (isPoleKind(mountKind)) {
        p = snapPoleCamera(S, { x: wx, y: wy }, model, { mountKind, h: store.newPoleHeight ?? 3 });
        if (!p) { onHint?.({ message: 'Click beside an exterior light or utility pole. Choose outdoor-rated hardware and a lens height below the pole top.' }); return; }
      } else if (store.snapCameras) {
        const mount = snapWallCamera({ ...S, models: [model] }, wx, wy, 2.8, 1.25, { modelId, mountKind });
        if (!mount) { onHint?.({ message: `Click within ${formatLength(1.25, S, 2)} of an allowed solid wall tall enough for a ${formatLength(2.8, S)} mount.` }); return; }
        p = mount;
      }
      const cam = store.addCamera(p.x, p.y, modelId, { ...p, mountKind, pan: p.pan ?? autoAim(p.x, p.y) });
      if (cam) {
        const m = S.models.find(q => q.id === cam.modelId);
        if (m && !m.pano) {
          const g = camGeom(S, cam);
          const recog = g ? g.m.resW / (S.opt.t.recognise * g.spread) : 10;
          cam.tilt = Math.min(75, Math.max(0, Math.atan2(Math.max(cam.h - S.opt.hFace, 0.1), Math.max(Math.min(recog, 12), 0.3)) * DEG));
        }
      }
      store.setTool('select');
      return;
    }

    if (tool === 'calib') {
      calib.current = { a: { sx, sy }, b: { sx, sy } };
      drag.current = { mode: 'calib' };
      return;
    }

    // polyline / polygon tools
    if (tool === 'stairs') { const stair = store.addStair(snapPoint(wx, wy)); onHint?.(stair ? { selected: store.sel } : { message: `Add another level at a different elevation first (up to ${formatLength(36, S)} rise). Limit: 100 stair connections.` }); return; }
    if (['light', 'equipment', 'obstacle', 'task', 'person', 'desk', 'chair', 'entrance', 'gate', 'vehicle', 'light-pole', 'utility-pole'].includes(tool)) { const object = placePlanObject(store, tool, snapPoint(wx, wy)); onHint?.(object ? { selected: store.sel } : { message: 'This plan has reached the limit for this type of object.' }); return; }
    if (['door', 'window', 'passage'].includes(tool)) {
      const display = store.groundViewDoc;
      const nearest = display.walls.slice().sort((a, b) => distToSeg(wx, wy, a.x1, a.y1, a.x2, a.y2) - distToSeg(wx, wy, b.x1, b.y1, b.x2, b.y2))[0];
      const selection = nearest && store.activateGroundSelection(groundSelection(display, { type: 'wall', id: nearest.id }));
      S = store.doc;
      const wall = selection && S.walls.find(w => w.id === selection.id);
      const opening = wall && distToSeg(wx, wy, wall.x1, wall.y1, wall.x2, wall.y2) < Math.max(0.2, 10 / v.k) && openingAt(S, wall, { x: wx, y: wy }, tool);
      if (!opening) { onHint?.({ message: 'Choose a wall with clear space for this opening, away from other openings and camera mounts.' }); return; }
      store.snapshot(); const o = { id: uid('o'), name: tool[0].toUpperCase() + tool.slice(1) + ' ' + (S.openings.length + 1), ...opening };
      S.openings.push(o); store.select({ type: 'opening', id: o.id }); store.setTool('select'); store.touch(); onHint?.({ selected: store.sel }); return;
    }
    if (!['wall', 'floor', 'exclude', 'zone', 'cable', 'outdoor', 'parking', 'road', 'building'].includes(tool)) return;
    if (tool === 'cable' && !draft.current) { onHint?.({ message: 'Select a camera and equipment on the Install tab, then choose Draw cable route.' }); return; }
    const closed = tool !== 'wall' && tool !== 'cable';
    let p = snapPoint(wx, wy);
    // Pressing and dragging with the building shell tool draws a rectangular room in one gesture.
    if (!draft.current && tool === 'building') drag.current = { mode: 'room', a: p, sx, sy };
    if (!draft.current) { draft.current = { kind: tool, pts: [], closed }; setDrawing(true); }
    else if (!e.shiftKey) p = orthoSnap(draft.current.pts.at(-1), snapPoint(wx, wy));
    if (closed && draft.current.pts.length > 2 &&
        Math.hypot(p.x - draft.current.pts[0].x, p.y - draft.current.pts[0].y) < 10 / v.k) {
      finishDraft();
      return;
    }
    const last = draft.current.pts.at(-1);
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 0.02) draft.current.pts.push(p);
    requestDraw();
  }

  function applyWallEdit(wall, patch) {
    const result = wallEdit(S, wall, patch);
    if (result.error) { store.reportEditError(result.error); return false; }
    Object.assign(wall, result.object);
    for (const c of result.cams) Object.assign(S.cams.find(q => q.id === c.id), c);
    for (const o of result.openings) Object.assign(S.openings.find(q => q.id === o.id), o);
    return true;
  }

  function onPointerMove(e) {
    const { sx, sy } = evPt(e);
    const v = view.current;
    const wx = WX(v, sx), wy = WY(v, sy);
    onCoords?.(wx, wy);

    const d = drag.current;
    if (d) {
      if (d.mode === 'pan') {
        v.ox = d.ox - (sx - d.sx) / v.k;
        v.oy = d.oy - (sy - d.sy) / v.k;
        requestDraw(); return;
      }
      if (d.mode === 'aim') {
        const oldAngle = d.cam[d.field];
        d.cam[d.field] = norm360(Math.atan2(wy - (d.anchor || d.cam).y, wx - (d.anchor || d.cam).x) * DEG);
        if (e.shiftKey) d.cam[d.field] = Math.round(d.cam[d.field] / 15) * 15;
        if (isPoleCamera(S, d.cam) && !poleCameraPose(S, d.cam)) d.cam[d.field] = oldAngle;
        store.touch(); return;
      }
      if (d.mode === 'vertex') {
        const p = snapPoint(wx, wy);
        if (store.sel.type === 'wall') {
          if (!applyWallEdit(d.obj, d.vi === 0 ? { x1: p.x, y1: p.y } : { x2: p.x, y2: p.y })) return;
        } else d.obj.pts[d.vi] = p;
        store.touch(); return;
      }
      if (d.mode === 'move') {
        const dx = wx - d.start.wx, dy = wy - d.start.wy;
        const g = S.opt.snap ? S.opt.grid : 0;
        const q = val => (g ? Math.round(val / g) * g : val);
        const o = d.obj;
        if (d.type === 'cam') {
          if (o.locked) return;
          if (isCeilingCamera(S, o)) {
            const mount = ceilingCameraPose(S, { ...o, x: q(d.orig.x + dx), y: q(d.orig.y + dy) });
            if (!mount) return;
            Object.assign(o, mount);
          } else if (isPoleCamera(S, o)) {
            const mount = snapPoleCamera(S, { x: d.orig.x + dx, y: d.orig.y + dy }, store.model(o.modelId), o);
            if (!mount) return;
            Object.assign(o, mount);
          } else if (store.snapCameras) {
            const mount = snapWallCamera(S, d.orig.x + dx, d.orig.y + dy, o.h, 1.25, o);
            if (!mount) return;
            o.x = mount.x; o.y = mount.y;
          } else { if (d.type === 'light') o.poleId = ''; o.x = q(d.orig.x + dx); o.y = q(d.orig.y + dy); }
        } else if (d.type === 'opening') {
          const w = S.walls.find(w => w.id === o.wallId), c = openingCentre(S, d.orig);
          const next = c && openingAt(S, w, { x: c.x + dx, y: c.y + dy }, o.kind, o); if (!next) return; Object.assign(o, next);
        } else if (['light', 'equipment', 'obstacle', 'task', 'target', 'stair'].includes(d.type)) { if (d.type === 'light') o.poleId = ''; o.x = q(d.orig.x + dx); o.y = q(d.orig.y + dy); }
        else if (d.type === 'wall') {
          const moveX=q(d.orig.x1+dx)-d.orig.x1,moveY=q(d.orig.y1+dy)-d.orig.y1;
          if (!applyWallEdit(o, { x1: d.orig.x1+moveX, y1: d.orig.y1+moveY, x2: d.orig.x2+moveX, y2: d.orig.y2+moveY })) return;
        } else if (d.type === 'bg') { o.x = d.orig.x + dx; o.y = d.orig.y + dy; }
        else o.pts = d.orig.pts.map(p => ({ x: q(p.x + dx), y: q(p.y + dy) }));
        store.touch(); return;
      }
      if (d.mode === 'calib') {
        calib.current.b = { sx, sy };
        requestDraw(); return;
      }
      if (d.mode === 'room') {
        if (!d.rect && Math.hypot(sx - d.sx, sy - d.sy) < 8) return;
        const b = snapPoint(wx, wy), a = d.a;
        d.rect = true; hover.current = null;
        if (draft.current?.kind === 'building') draft.current.pts = [a, { x: b.x, y: a.y }, b, { x: a.x, y: b.y }];
        requestDraw(); return;
      }
    }

    if (draft.current) {
      const raw = snapPoint(wx, wy);
      hover.current = e.shiftKey ? raw : orthoSnap(draft.current.pts.at(-1), raw);
      requestDraw();
    }
  }

  function onPointerUp() {
    const d = drag.current;
    if (d?.mode === 'calib' && calib.current) {
      const { a, b } = calib.current;
      const px = Math.hypot(b.sx - a.sx, b.sy - a.sy);
      if (px > 12) {
        onHint?.({
          calibrate: {
            pixels: px,
            metresNow: px / view.current.k,
            anchor: { x: WX(view.current, a.sx), y: WY(view.current, a.sy) }
          }
        });
      }
      calib.current = null;
    }
    drag.current = null;
    if (d?.mode === 'room' && d.rect && draft.current?.kind === 'building') {
      const [a, , b] = draft.current.pts;
      if (Math.min(Math.abs(b.x - a.x), Math.abs(b.y - a.y)) < 0.5) { draft.current.pts = [a]; onHint?.({ message: 'Drag further to draw a room at least 0.5 m wide, or click each corner in turn.' }); }
      else {
        draft.current.name = 'Room ' + (S.areas.filter(area => area.kind === 'floor').length + 1);
        const name = draft.current.name; finishDraft();
        if (!draft.current) { store.setTool('select'); onHint?.({ roomDrawn: name }); }
      }
    }
    requestDraw();
  }

  function finishDraft() {
    const d = draft.current;
    setDrawing(false);
    draft.current = null;
    hover.current = null;
    if (!d) return;
    const error = finishPlanDraft(store, d);
    if (error) { draft.current = d; setDrawing(true); onHint?.({ message: error }); requestDraw(); return; }
    if (d.kind === 'cable') store.setTool('select');
    onHint?.({ selected: store.sel });
    requestDraw();
  }

  /* ---- keyboard ---- */
  useEffect(() => {
    function onKeyDown(e) {
      if (!active || document.querySelector('dialog[open]')) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (isUiEvent(e)) return;
      if (e.code === 'Space') { spaceDown.current = true; e.preventDefault(); return; }
      if (e.key === 'Escape') {
        if (draft.current) { draft.current = null; setDrawing(false); requestDraw(); }
        else store.select(null);
        return;
      }
      if (readOnly) return;
      if (e.key === 'Enter') { if (draft.current) finishDraft(); return; }
      if (e.key === 'Delete' || e.key === 'Backspace') { store.remove(); e.preventDefault(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.shiftKey ? store.redo() : store.undo(); e.preventDefault(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') { store.redo(); e.preventDefault(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd' && store.sel?.type === 'cam') {
        store.duplicateCam(); e.preventDefault(); return;
      }
      if (store.sel?.type === 'cam' && !e.ctrlKey && !e.metaKey) {
        const c = store.selected();
        if (!c || c.locked) return;
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', '[', ']'].includes(e.key)) return;
        store.snapshot();
        const before = { ...c };
        const step = e.shiftKey ? 1 : 0.1;
        if (e.key === 'ArrowLeft') c.x -= step;
        else if (e.key === 'ArrowRight') c.x += step;
        else if (e.key === 'ArrowUp') c.y -= step;
        else if (e.key === 'ArrowDown') c.y += step;
        else if (e.key === '[') c.pan = norm360(c.pan - 5);
        else if (e.key === ']') c.pan = norm360(c.pan + 5);
        else return;
        if (isCeilingCamera(S, c) && e.key.startsWith('Arrow')) {
          const mount = ceilingCameraPose(S, c);
          if (mount) Object.assign(c, mount); else Object.assign(c, before);
        } else if (isPoleCamera(S, c)) {
          const mount = e.key.startsWith('Arrow') ? snapPoleCamera(S, c, store.model(c.modelId), c) : poleCameraPose(S, c);
          Object.assign(c, mount || before);
        } else if (store.snapCameras && e.key.startsWith('Arrow')) {
          const mount = snapWallCamera(S, c.x, c.y, c.h, 1.25, c);
          c.x = mount?.x ?? before.x; c.y = mount?.y ?? before.y;
        }
        e.preventDefault();
        store.touch();
      }
    }
    function onKeyUp(e) { if (e.code === 'Space') spaceDown.current = false; }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  });

  const cursor = store.tool === 'pan' ? 'pan' : store.tool === 'select' || readOnly ? '' : 'draw';

  return (
    <div className="stage" ref={wrapRef}>
      {drawing && !readOnly && <div className="hud draft-actions" role="group" aria-label="Drawing controls"><span>{store.tool === 'cable' ? 'Cable route' : 'Drawing'} in progress</span><button className="btn sm primary" onClick={finishDraft}>Finish drawing</button><button className="btn sm" onClick={() => { draft.current = null; hover.current = null; setDrawing(false); requestDraw(); canvasRef.current?.focus({ preventScroll: true }); }}>Cancel drawing</button></div>}
      <canvas
        ref={canvasRef} tabIndex={0} aria-label="2D plan editor"
        className={cursor}
        onPointerDown={e => { e.currentTarget.focus({ preventScroll: true }); onPointerDown(e); }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => !readOnly && draft.current && finishDraft()}
        onWheel={e => { const { sx, sy } = evPt(e); zoomAt(sx, sy, e.deltaY < 0 ? 1.12 : 1 / 1.12); }}
      />
    </div>
  );
});

export default Stage;
